import { useQuery } from '@tanstack/react-query'
import cx from 'classnames'
import dayjs from 'dayjs'
import { sortBy } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import useBackendClient from '~/api/BackendClient/useBackendClient'
import type {
  PlatePage_CulturePlate,
  PlatePage_ObservationHistory,
} from '~/api/backend/pages/platePageAPI'
import { QUERY_KEYS } from '~/api/query-client-keys'
import type { BackendHTTPError } from '~/api/shared/BackendHTTPError'
import Breadcrumbs from '~/components/Breadcrumbs'
import { FullPageError } from '~/components/Errors'
import PageNotFound from '~/components/PageNotFound'
import PlateAbsorbanceTabbedSidebar from '~/components/PlateAbsorbanceTabbedSidebar'
import PlateConcentrationTabbedSidebar from '~/components/PlateConcentrationTabbedSidebar'
import PlateHero from '~/components/PlateHero'
import Select, { type SelectOption } from '~/components/Select'
import ZeroWidthSpaceToken from '~/components/ZeroWidthSpaceToken'
import { analytics } from '~/core/analytics'
import { useFeatureFlag } from '~/core/featureFlags'
import { useDemoQuery } from '~/demoControls/DemoContext'
import { cn } from '~/lib/utils'
import { useMetrics } from '~/pages/Monitor/metrics'
import { decodeURITimestamp, encodeURITimestamp } from '~/utils/date'
import PlateConfluenceGraphSidebar from './PlateConfluenceGraphSidebar'
import PlateMetadataSidebar from './PlateMetadataSidebar'
import cs from './plate.scss'
import { usePreloadAdjacentPlateObservations } from './usePlatePagePreloading'
import LateralNavCompass, {
  LateralNavAxisMode,
  LateralNavDirection,
} from './well/LateralNavCompass'

export type WellOverlay = { key: string; label: string }

export default function PlatePage({ id }: { id: string }) {
  const demoData = useDemoQuery('monitor', 'PlatePage')?.({ id })
  const client = useBackendClient()

  const { isLoading, error, data } = useQuery({
    queryFn: async () => await client.platePage.getData(id),
    queryKey: [QUERY_KEYS.PLATE_PAGE, id],
  })

  if (demoData?.culturePlate) {
    return <Content plate={demoData.culturePlate} />
  }

  if (isLoading) {
    return <LoadingPlaceholder id={id} />
  }

  if (error) {
    return <FullPageError error={error as BackendHTTPError} />
  }

  const plate = data?.culturePlate
  if (plate == null) {
    return <PageNotFound />
  }
  return <Content plate={plate} />
}

type Plate = PlatePage_CulturePlate
type PlateObservation = Plate['plateObservationHistory'][0]

function Content({ plate }: { plate: Plate }) {
  const history = useHistory()
  const hackathon2025 = useFeatureFlag('hackathon2025')
  const metrics = useMetrics()

  const sortedObservationHistory = useMemo(
    () => sortBy(plate.plateObservationHistory as PlateObservation[], 'timestamp'),
    [plate.plateObservationHistory],
  )

  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const timestampParam = params.get('t')
  const decodedTime = decodeURITimestamp(timestampParam)
  const decodedTimeISOString = decodedTime?.toISOString()
  let observationIndex = decodedTimeISOString
    ? sortedObservationHistory.findIndex(obs => obs.timestamp >= decodedTimeISOString)
    : -1
  if (observationIndex < 0) {
    observationIndex = sortedObservationHistory.length - 1
  }
  const observation = sortedObservationHistory[observationIndex] as
    | PlateObservation
    | undefined

  useEffect(() => {
    analytics.page('Monitor', 'Plate', {
      plateId: plate.id,
      numPlateObservations: plate.plateObservationHistory.length,
      plateObservationTime: observation?.timestamp ?? null,
    })
  }, [plate.id, plate.plateObservationHistory.length, observation?.timestamp])

  const [wellOverlay, setWellOverlay] = useState<WellOverlay>({
    key: 'status',
    label: 'Culture Status',
  })

  const [highlightedWell, setHighlightedWell] = useState<string | undefined>(undefined)

  // Track when current observation's sprite finishes loading for preloading trigger
  // PlateHero notifies us via onSpriteLoadingChange when useExtractedPlateSprite completes
  const [spriteLoaded, setSpriteLoaded] = useState(false)
  const handleSpriteLoadingChange = useCallback((isLoading: boolean) => {
    if (!isLoading) {
      setSpriteLoaded(true)
    }
  }, [])

  // Reset sprite loaded state when observation changes
  useEffect(() => {
    // biome doesn't count "reset-on-change" patterns as a dependency usage.
    // This makes the intent explicit and keeps the dependency accurate.
    void observationIndex
    setSpriteLoaded(false)
  }, [observationIndex])

  // Preload adjacent observations (prev/next) for instant navigation
  // Starts when spriteLoaded becomes true (adaptive timing, no fixed delays)

  usePreloadAdjacentPlateObservations(
    sortedObservationHistory,
    observationIndex,
    spriteLoaded,
  )

  return (
    <div className='w-full h-full flex flex-col'>
      <div className='font-text h-[52px] border-b border-grey-lightest px-10 flex items-center shrink-0'>
        <Breadcrumbs plate={{ id: plate.id, data: plate }} className={cs.breadcrumbs} />
      </div>

      <div className='flex h-full gap-8 px-10'>
        <div className='flex flex-col w-full h-full flex-grow px-4'>
          {/* Title and Toolbar */}
          <div className='flex flex-col gap-4'>
            <div
              className={cn(
                'font-title text-grey-dark flex items-center gap-4 mt-5',
                hackathon2025 && 'flex-wrap',
              )}
            >
              <span>{plate.barcode}</span>
              <PlateStatus plate={plate} />
              <Select<SelectOption>
                className={cs.selection}
                // @ts-expect-error
                items={[
                  {
                    label: 'Culture Status',
                    key: 'status',
                  },
                  !hackathon2025
                    ? {
                        label: 'Confluence (%)',
                        key: 'confluence',
                      }
                    : null,
                  // We hide the absorbance option if the data doesn't have absorbance yet
                  hackathon2025
                    ? {
                        label: 'Absorbance',
                        key: 'absorbance',
                      }
                    : null,
                  // We hide the absorbance option if the data doesn't have absorbance yet
                  hackathon2025
                    ? {
                        label: 'Concentration',
                        key: 'concentration',
                      }
                    : null,
                  !hackathon2025
                    ? {
                        label: 'Hidden',
                        key: 'none',
                      }
                    : null,
                ].filter(Boolean)}
                itemKey={'key'}
                activeItem={wellOverlay}
                onChange={setWellOverlay}
                triggerClassName={cs.selectContainer}
              />
            </div>
            <div className='mb-2.25'>
              <HeroToolbar
                observation={observation}
                page={{
                  number: observationIndex + 1,
                  total: sortedObservationHistory.length,
                }}
                onNavigate={dir => {
                  let newIndex: number
                  if (dir === LateralNavDirection.LEFT) {
                    newIndex = Math.max(observationIndex - 1, 0)
                    metrics.plate.observationNavigate(plate.id, 'prev')
                  } else {
                    newIndex = Math.min(
                      observationIndex + 1,
                      sortedObservationHistory.length - 1,
                    )
                    metrics.plate.observationNavigate(plate.id, 'next')
                  }
                  const newTimestamp = sortedObservationHistory[newIndex].timestamp
                  const newParams = new URLSearchParams(params)
                  newParams.set('t', encodeURITimestamp(dayjs(newTimestamp)))
                  history.replace({ search: newParams.toString() })
                }}
              />
            </div>
          </div>
          <PlateHero
            plate={plate}
            observationTimestamp={observation?.timestamp}
            wellOverlay={wellOverlay}
            highlightedWell={highlightedWell}
            onWellHover={setHighlightedWell}
            onSpriteLoadingChange={handleSpriteLoadingChange}
            onWellClick={well => metrics.plate.wellClick(plate.id, well)}
          />
        </div>

        <div
          className={cn(
            // By default
            'w-[330px] pt-[100px]',
            // Specificially for the hackathon
            hackathon2025 && 'w-[700px] pt-8',
            hackathon2025 &&
              ['absorbance', 'concentration'].includes(wellOverlay.key) &&
              'w-[900px]',
          )}
        >
          {(() => {
            if (
              wellOverlay.key === 'absorbance' ||
              (hackathon2025 && wellOverlay.key === 'status')
            ) {
              return (
                <PlateAbsorbanceTabbedSidebar
                  plate={plate}
                  highlightedWell={highlightedWell}
                  observation={observation as PlatePage_ObservationHistory}
                />
              )
            }

            if (wellOverlay.key === 'concentration') {
              return (
                <PlateConcentrationTabbedSidebar
                  plate={plate}
                  highlightedWell={highlightedWell}
                  observation={observation as PlatePage_ObservationHistory}
                />
              )
            }

            return (
              <>
                <PlateConfluenceGraphSidebar plate={plate} />
                <PlateMetadataSidebar plate={plate} />
              </>
            )
          })()}
        </div>
      </div>
    </div>
  )
}

function LoadingPlaceholder({ id }: { id: string }) {
  // TODO(SWE-1840): Render cached data here when possible.

  return (
    <div className='w-full h-full flex flex-col'>
      <div className='font-text h-[52px] border-b border-grey-lightest px-10 flex items-center shrink-0'>
        <Breadcrumbs plate={{ id, data: null }} className={cs.breadcrumbs} />
      </div>

      <div className='flex h-full gap-8 px-10'>
        <div className='flex flex-col w-full h-full flex-grow px-4'>
          {/* Title and Toolbar */}
          <div className='flex flex-col gap-4'>
            <div className='font-title text-grey-dark mt-5'>
              <ZeroWidthSpaceToken />
            </div>
            <div className='mb-2.25'>
              <HeroToolbar loading />
            </div>
          </div>
        </div>

        <div className='w-[330px] pt-[100px]'>{/* Sidebar placeholder */}</div>
      </div>
    </div>
  )
}

function PlateStatus(props: { plate: PlatePage_CulturePlate }) {
  const plate = props.plate as unknown as PlatePage_CulturePlate
  const statuses = new Set(plate.wellCultures.map(c => c.status))

  // Some wells are active
  if (statuses.has('Active')) {
    return <span className={cx(cs.status, cs.checkedIn)}>Checked In</span>
  }

  // All wells are either consumed or terminated
  return <span className={cs.status}>Inactive</span>
}

function HeroToolbar({
  observation,
  page,
  loading,
  onNavigate,
}: {
  observation?: PlateObservation
  page?: { number: number; total: number }
  loading?: boolean
  onNavigate?: (dir: LateralNavDirection.LEFT | LateralNavDirection.RIGHT) => void
}) {
  const time = observation ? dayjs(observation.timestamp) : null
  const isThisYear = time?.year() === dayjs().year()
  const hackathon2025 = useFeatureFlag('hackathon2025')

  const enabledDirections = [
    page != null && page.number > 1 ? LateralNavDirection.LEFT : null,
    page != null && page.number < page.total ? LateralNavDirection.RIGHT : null,
  ].filter(d => d != null)

  return (
    <div className={cs.heroToolbarContent}>
      <div>
        {time ? (
          <>
            {time.format(isThisYear ? 'MMM D' : 'MMM D, YYYY')}&ensp;
            {time.format('h:mm A')} ({time.fromNow()})
          </>
        ) : loading ? (
          <>&nbsp;</>
        ) : hackathon2025 ? (
          'No data yet.'
        ) : (
          'No images yet.'
        )}
      </div>

      <div className={cs.pager}>
        {page?.total ? (
          <span>
            Observation {page.number} of {page.total}
          </span>
        ) : page ? (
          <span>No observations</span>
        ) : null}

        <LateralNavCompass
          axes={LateralNavAxisMode.HORIZONTAL}
          onNavigate={d => {
            onNavigate?.(d as LateralNavDirection.LEFT | LateralNavDirection.RIGHT)
          }}
          enable={enabledDirections}
        />
      </div>
    </div>
  )
}
